<script>
import StarIcon from "@/components/icons/StarIcon.vue";
import MtLoadingAnimationLinear from "@/components/UI/mtLoadingAnimationLinear/mtLoadingAnimationLinear.vue";
import LinkIcon from "@/components/icons/LinkIcon.vue";
import ConfirmationDialog from "@/components/composite/confirmationDialog/confirmationDialog.vue";
import TrashIcon from "@/components/icons/TrashIcon.vue";
import AppTextarea from "@/components/UI/AppTextarea/AppTextarea.vue";
import SendIcon from "@/components/icons/SendIcon.vue";
import MtButton from "@/components/UI/mtButton/index.vue";
import {mapGetters} from "vuex";
import FileDownloadCard from "@/components/workspace/assessmentPlans/plans/fileDownloadCard/index.vue";
import AppDataTable from "@/components/UI/AppDataTable/AppDataTable.vue";
import AppTooltip from "@/components/UI/AppTooltip/AppTooltip.vue";
import InfoBlock from "@/components/workspace/InfoBlock.vue";
import AppUploadedFile from "@/components/UI/AppUploadedFile.vue";
import useRespondentsStore from "@/store/respondents.store";
import customAssessmentApi from "@/api/custom-assessment.api";
import usePlansStore from "@/store/plans.store";
import {storeToRefs} from "pinia";
import useAssessmentChatStore from "@/store/assessment-chat.store";
import {formatDateTimeByDefault} from "@/services/date.js";
import CloseIcon from "@/components/UI/mtSvg/svg/CloseIcon.vue";
import MediaApi from "@/api/media.api";

export default {
  name: 'CustomAssessmentChat',
  components: {
    CloseIcon,
    AppUploadedFile,
    InfoBlock,
    AppTooltip,
    AppDataTable,
    SendIcon,
    LinkIcon,
    MtButton,
    AppTextarea,
    ConfirmationDialog,
    TrashIcon,
    FileDownloadCard,
    MtLoadingAnimationLinear,
    StarIcon
  },
  props: {
    sessionUuid: String
  },
  data: () => ({
    editingMessage: '',
    newMessage: {
      uuid: '',
      message: '',
      files: []
    },
    pendingActionAfterReset: null,
    isMessageSending: false,
    isConfirmationDialogOpen: false,
    errors: {
      message: []
    },
    deletingFiles: []
  }),
  setup() {
    const respondentsStore = useRespondentsStore();

    const assessmentChatStore = useAssessmentChatStore();
    const {messages, isMessagesLoading, isMessageSending} = storeToRefs(assessmentChatStore);

    const plansStore = usePlansStore();
    const {plan} = storeToRefs(plansStore);

    return {
      respondentsStore,
      assessmentPlan: plan,
      assessmentChatStore,
      messages,
      isMessagesLoading
    }
  },
  computed: {
    ...mapGetters(['authUser', 'isRespondent']),

    task() {
      const tests = this.assessmentPlan.tests ?? this.assessmentPlan.structure;
      return tests?.find((item) => item.uuid === this.$route.params.taskUuid) ?? null;
    },

    allowedOnlyUploadFiles() {
      return !this.task?.input_text && this.task?.upload_files;
    },

    messageFieldPlaceholder() {
      if (this.isRespondent) {
        if (this.allowedOnlyUploadFiles) {
          return 'Разрешена только загрузка файлов';
        }

        if (!this.task?.input_text) {
          return 'Вам запрещено отправлять сообщения';
        }
      }

      return 'Отправить сообщение ...';
    }
  },
  methods: {
    formatDateTimeByDefault,
    checkToExistingNewMessage(action) {
      if (this.newMessage.message || this.newMessage.files.length) {
        this.isConfirmationDialogOpen = true;
        this.pendingActionAfterReset = action;
      } else {
        action();
      }
    },
    confirm() {
      this.isConfirmationDialogOpen = false;
      this.pendingActionAfterReset();
    },
    uploadFiles(event) {
      let files = [];

      event.target.files.forEach((file) => {
        if (file.size > 10485760) {
          this.$toast.error(`Файл ${file.name} превышает максимальный размер 10 МБ`);
          return;
        }

        files.push(file);
      })

      this.newMessage.files = [...this.newMessage.files, ...files];

      this.errors.message = [];
    },
    deleteFilesHandler(file, index) {
      this.newMessage.files.splice(index, 1);
      this.deletingFiles.push(file.uuid);
    },
    scrollToEndOfChat() {
      this.$nextTick(() => {
        const chat = this.$refs.chat;
        chat?.scroll(0, chat.scrollHeight);
      });
    },

    async loadMessages() {
      return await this.assessmentChatStore.getMessages(
          this.$route.params.planUuid,
          this.$route.params.taskUuid,
          this.sessionUuid
      ).then(() => {
        this.$nextTick(() => {
          this.scrollToEndOfChat();
        })
      })
    },
    async sendMessage() {
      this.clearErrors();

      if (this.newMessage.files.length === 0 && !this.newMessage.message) {
        this.errors.message = ['Пожалуйста, заполните поле или выберите файл для загрузки.'];
        return;
      }

      let session = this.sessionUuid;
      if (!session) {
        if (this.isRespondent) {
          session = await customAssessmentApi.startSession(this.$route.params.planUuid, this.$route.params.taskUuid);
        } else {
          const {data} = await this.respondentsStore.getRespondentSession(this.$route.params.planUuid, this.$route.params.taskUuid, this.authUser.uuid);
          session = data;
        }
      }
      if(this.deletingFiles.length) {
        this.newMessage.deleting_files_uuid = this.deletingFiles
      }

      this.assessmentChatStore.sendMessage(
          this.$route.params.planUuid,
          this.$route.params.taskUuid,
          session,
          this.newMessage
      )
          .then((res) => {
            this.newMessage.uuid = '';
            this.newMessage.message = '';
            this.newMessage.files = [];
            this.editingMessage = '';

            this.scrollToEndOfChat()
            setTimeout(() => {
              this.$refs.textarea.calculateHeight();
            })
          })
          .catch((error) => {
            if (error?.data?.message) {
              this.$toast.error(error?.data?.message)
            }
          })
    },

    editMessageHandler(message) {
      this.clearErrors();
      this.newMessage.uuid = message.uuid;
      this.newMessage.message = message.value;
      this.newMessage.files = JSON.parse(JSON.stringify(message.files));
      this.editingMessage = message.value;
      this.deletingFiles = [];
    },

    deleteMessageHandler(message) {
      this.assessmentChatStore.deleteMessage(message?.uuid)
      .then((res) => {
        this.$toast.success('Сообщение удалено')
      })
      .catch((error) => {
        if (error?.data?.message) {
          this.$toast.error(error?.data?.message)
        }
      })
    },

    canUpdateMessage(message) {
      const oneHourInMs = 60 * 60 * 1000;
      const createdAt = new Date(message.createdAt);
      const now = new Date();

      const diff = now.getTime() - createdAt.getTime();
      const createdInHour = diff <= oneHourInMs;

      return this.authUser.uuid === message.user.uuid && createdInHour;
    },

    cancelEditingMessage() {
      this.clearErrors();
      this.editingMessage = '';
      this.newMessage = {
        uuid: '',
        message: '',
        files: [],
      }
    },

    clearErrors() {
      this.errors = {
        message: []
      }
    }
  },
  mounted() {
    this.loadMessages();
  },
  unmounted() {
    this.assessmentChatStore.clearState();
  },
  watch: {
    sessionUuid() {
      this.loadMessages();
    }
  }
}
</script>

<template>
  <div class="custom-assessment__chat">
    <mt-loading-animation-linear v-if="isMessagesLoading" />

    <template v-else>
      <info-block
          class="custom-assessment__chat__no-data"
          v-if="!messages.length"
          title="Обсуждение еще не начато"
      />

      <div
          ref="chat"
          v-if="messages.length"
          class="custom-assessment__chat__list"
      >
        <div
            :class="[
                'custom-assessment__chat__item',
                {
                  'custom-assessment__chat__item_me': message.user.uuid === authUser.uuid
                }
            ]"
            v-for="(message, index) in messages"
            :key="message.uuid"
        >
          <div class="custom-assessment__chat__item__header">
            <h3 class="custom-assessment__chat__item__title">{{ message.user.fullName }} ({{ message.user.role }})</h3>
            <p class="custom-assessment__chat__item__date">{{ formatDateTimeByDefault(message.createdAt) }}</p>
          </div>
          <p class="custom-assessment__chat__item__text">{{ message.value }}</p>

          <app-uploaded-file
              v-for="(file, index) in message.files"
              :key="index"
              :file
              url-key="originalUrl"
              name-key="fileName"
              bg-color="#fff"
          />

          <button
            v-if="canUpdateMessage(message) && index === messages.length - 1"
            class="custom-assessment__chat__item__edit"
            @click="editMessageHandler(message)"
          >
            Редактировать
          </button>

          <button
            v-if="canUpdateMessage(message) && index === messages.length - 1"
            class="custom-assessment__chat__item__edit"
            @click="deleteMessageHandler(message)"
          >
            Удалить
          </button>
        </div>
      </div>

      <div
          v-if="newMessage.uuid"
          class="custom-assessment__chat__reply"
      >
        <div class="custom-assessment__chat__reply__desc">
          <h4>Редактировать сообщение</h4>
          <p>
            {{ editingMessage }}
          </p>
        </div>

        <mt-button
            icon
            class="custom-assessment__chat__reply__cancel-btn"
            @click="cancelEditingMessage"
        >
          <close-icon />
        </mt-button>
      </div>

      <div class="custom-assessment__input-box">
        <input
            ref="newMessageFileInput"
            type="file"
            multiple="multiple"
            style="display: none;"
            @change="uploadFiles"
        />
        <mt-loading-animation-linear
            :class="[
              'custom-assessment__message-send-loading',
              {
                'custom-assessment__message-send-loading_show': isMessageSending
              }
            ]"
        />

        <div
            v-if="newMessage.files.length"
            class="custom-assessment__input-box__files"
        >
          <h4 class="custom-assessment__input-box__files__title">Файлы для отправки</h4>
          <app-uploaded-file
              v-for="(file, index) in newMessage.files"
              :key="index"
              bg-color="#fff"
              name-key="name"
              :file="file"
              :delete-fn="() => deleteFilesHandler(file, index)"
          />
        </div>

        <div class="custom-assessment__input-box__wrap">
          <app-textarea
              v-model="newMessage.message"
              ref="textarea"
              :disabled="!task?.input_text && isRespondent"
              :placeholder="messageFieldPlaceholder"
              class="custom-assessment__input-box__textarea"
              :loading="isMessageSending"
              :init-height="40"
              submit-on-enter
              :errors="errors.message"
              @submit="sendMessage()"
          />
          <mt-button
              v-if="isRespondent ? task?.upload_files : true"
              icon
              color="white"
              class="custom-assessment__input-box__btn"
              @click="$refs.newMessageFileInput.click()"
          >
            <link-icon />
          </mt-button>
          <mt-button
              icon
              color="white"
              class="custom-assessment__input-box__btn"
              :disabled="isMessageSending"
              @click="sendMessage()"
          >
            <send-icon />
          </mt-button>
        </div>
      </div>
    </template>

    <confirmation-dialog
        v-model="isConfirmationDialogOpen"
        text="Вы уверены, что хотите перейти к другому заданию?"
        submit-text="Перейти"
        :handler="confirm"
    >
      <template #title>
        Введенное вами сообщение будет потеряно
      </template>
    </confirmation-dialog>
  </div>
</template>

<style lang="scss">
.custom-assessment__chat {
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;

  &__list {
    margin-bottom: auto;
  }

  &__no-data {
    margin: auto 0;
  }

  &__reply {
    display: flex;
    align-items: center;
    background: #F0F1F7;
    border-radius: 10px;
    padding: 10px 15px;
    gap: 15px;

    &__desc {
      flex-grow: 1;
    }

    h4 {
      color: $green;
      font-size: 14px;
      margin-bottom: 5px;
    }

    p {
      font-size: 14px;
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
    }
  }

  &__item {
    margin-bottom: 10px;
    padding: 15px;
    background: $light-gray;
    border-radius: 12px;

    &_me {
      background-color: $light-green;
    }

    &_mark {
      background-color: $light-green;
    }

    &__header {
      display: flex;
      font-weight: 400;
      font-size: 12px;
      line-height: 20px;
      letter-spacing: 0.2px;
      margin-bottom: 5px;
    }

    &__title {
      flex-grow: 1;
      color: #00788C;
    }

    &__star {
      color: $green;
      margin-right: 8px;
    }

    &__date {
      white-space: nowrap;
      color: #9FA2B4;
    }

    &__text {
      font-weight: 400;
      font-size: 14px;
      line-height: 20px;
      letter-spacing: 0.2px;
      color: #252733;
      margin-top: 5px;
      white-space: pre-wrap;
      word-break: break-word;

      &:not(&:last-child) {
        margin-bottom: 10px;
      }
    }

    &__chips-row {
      display: flex;
      flex-wrap: wrap;
      margin: 10px -5px 0;

      & + & {
        margin-top: 5px;
        margin-bottom: 5px;
      }
    }

    &__chip {
      display: flex;
      margin: 5px;
      background-color: #fff;
      padding: 10px 15px;
      border-radius: 12px;
      font-weight: 600;
      font-size: 14px;
      color: #00788C;
      align-items: center;

      span {
        margin-left: 8px;
        padding: 2px 5px;
        border: 2px solid #00788C;
        border-radius: 3px;
        line-height: 1;
        font-weight: bold;
      }
    }
  }
}
</style>